// Sentry Composable
// Provides methods to interact with Sentry: setUser, captureException, etc.

import * as Sentry from '@sentry/nuxt'

/**
 * Sentry composable for manual error tracking and user management
 * Use this when you need to manually capture errors or manage user context
 */
export const useSentry = () => {
  /**
   * Set user context for all subsequent errors
   * Call this when user logs in
   */
  const setUser = (userId: string, email?: string) => {
    Sentry.setUser({
      id: userId,
      email: email,
    })
  }

  /**
   * Clear user context
   * Call this when user logs out
   */
  const clearUser = () => {
    Sentry.setUser(null)
  }

  /**
   * Manually capture an exception
   * @param error - The error to capture
   */
  const captureException = (error: Error) => {
    Sentry.captureException(error)
  }

  /**
   * Manually capture a message
   * @param message - The message to capture
   * @param level - The severity level (default: info)
   */
  const captureMessage = (
    message: string,
    level: 'fatal' | 'error' | 'warning' | 'log' | 'debug' | 'info' = 'info'
  ) => {
    Sentry.captureMessage(message, level)
  }

  /**
   * Set a custom tag for filtering in Sentry
   * @param key - Tag name
   * @param value - Tag value
   */
  const setTag = (key: string, value: string) => {
    Sentry.setTag(key, value)
  }

  return {
    setUser,
    clearUser,
    captureException,
    captureMessage,
    setTag,
  }
}

